import { router, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TaskData } from "@/components/TaskRow";
import BucketMoveIcon from "@/components/BucketMoveIcon";
import AiActionMenu, { type AiActionEntry } from "@/components/AiActionMenu";
import QuickDuePicker from "@/components/QuickDuePicker";
import Linkified from "@/components/Linkified";
import { buildListOptions } from "@/lib/listOptions";
import { neighborsForReorder } from "@/lib/reorder";
import { todayIso } from "@/lib/dates";
import { userToday } from "@/stores/taskStore";
import { subtaskCount } from "@/lib/plural";
import { NOTES_PLACEHOLDER } from "@/lib/notesPlaceholder";
import { rootZenScope, stillInZenScope, type ZenScope } from "@/lib/zenScope";
import {
  toggleTask as mutateToggle,
  deleteTask as mutateDelete,
  updateTask,
  reorderTask,
  aiRun,
  aiPreview,
  aiApply,
  type PreviewOp,
  type AiAnchor,
} from "@/mutations/tasks";
import type { SharedProps } from "@/types";
import { useLocalView } from "@/hooks/useLocalView";
import { isOfflineStandIn, OFFLINE_EMPTY_TEXT } from "@/lib/swClient";
import { zenFrame, type ViewSource, type ZenFrameRequest } from "@/lib/views";

interface FrameProp {
  kind: "myDay" | "list" | "task";
  label: string;
  contextId: string | null;
}

interface Props {
  frame?: FrameProp;
  tasks?: TaskData[];
  fetchedAt?: string;
}

interface SessionEntry {
  frame: FrameProp;
  tasks: TaskData[];
}

interface Frame {
  label: string;
  scope: ZenScope;
  tasks: TaskData[];
  index: number;
  completedCount: number;
}

function makeFrame(label: string, scope: ZenScope, tasks: TaskData[]): Frame {
  return { label, scope, tasks, index: 0, completedCount: 0 };
}

function settleInFrame(f: Frame, id: string, saved: Partial<TaskData>, today: string): Frame {
  const at = f.tasks.findIndex((t) => t.id === id);
  if (at < f.index) return f;
  const merged = { ...f.tasks[at], ...saved };
  const leaves = !stillInZenScope(merged, f.scope, today);
  const tasks = f.tasks.map((t, i) => (i === at ? merged : t));
  if (!leaves) return { ...f, tasks };
  if (at === f.index) return { ...f, tasks, index: f.index + 1, completedCount: f.completedCount + 1 };
  return { ...f, tasks: tasks.filter((_, i) => i !== at) };
}

// After Enter confirms an inline title edit, the page-level "Enter = complete"
// shortcut is re-armed instantly. Ignore Enter for this long afterwards so the
// tail of the same gesture (a habitual second tap) doesn't complete the task.
const EDIT_CONFIRM_GRACE_MS = 500;

const DONE_MESSAGES = [
  "Готово. Отличная работа!",
  "Входящее пусто ✨",
  "Всё чисто 🌿",
  "Ничего не осталось — выдохни",
  "На сейчас всё ☕",
  "Список пуст, жизнь полна",
  "Впереди ясное небо",
  "Время выдохнуть 🌬",
  "Ты всё разгрёб 🙌",
  "На фронте задач тишина",
  "Иди поживи немного",
];

// ZenController#resolve_frame's precedence over the query.
function frameRequestFromUrl(url: string): ZenFrameRequest | null {
  const query = new URLSearchParams(url.split("?")[1] ?? "");
  const taskId = query.get("task_id");
  if (taskId) return { kind: "task", taskId };
  const listId = query.get("list_id");
  if (listId) return { kind: "list", listId };
  if (query.get("my_day") === "1") return { kind: "myDay" };
  if (query.get("my_day") === "in_order") return { kind: "inOrder" };
  return null;
}

export default function ZenShow(props: Props) {
  const url = usePage<SharedProps>().url;
  const request = useMemo(() => frameRequestFromUrl(url), [url]);
  const selectFrame = useCallback(
    (source: ViewSource): SessionEntry | null => {
      const found = request && zenFrame(source, request);
      if (!found) return null;
      const { tasks, ...frame } = found;
      return { frame, tasks: tasks as TaskData[] };
    },
    [request],
  );
  const local = useLocalView("zen", selectFrame);
  const fromProps = props.frame && props.tasks ? { frame: props.frame, tasks: props.tasks } : null;
  // The queue is taken once, at entry: a later store update must not move the user's place in it.
  const [entry, setEntry] = useState<SessionEntry | null>(local ?? fromProps);
  const available = local ?? fromProps;
  if (!entry && available) setEntry(available);

  if (!entry) {
    return (
      <div className="zen-screen" data-testid="zen-screen">
        <ExitButton />
        {isOfflineStandIn(props.fetchedAt) && <p className="zen-offline-hint">{OFFLINE_EMPTY_TEXT}</p>}
      </div>
    );
  }
  return <ZenSession frame={entry.frame} tasks={entry.tasks} />;
}

function exitZen() {
  router.visit("/my-day");
}

function ExitButton() {
  return (
    <button className="zen-close" onClick={exitZen} aria-label="Выйти">
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

function ZenSession({ frame, tasks }: SessionEntry) {
  const sharedProps = usePage<SharedProps>().props;
  const aiActions = sharedProps.aiActions ?? [];
  const taskActions = aiActions.filter((a) => a.scope === "task");
  const listOptions = useMemo(() => buildListOptions(sharedProps.sidebar), [sharedProps.sidebar]);
  // `frame`/`tasks` seed this only at mount — there's no effect re-syncing `stack` from
  // a later, fresher render (the IndexedDB snapshot resolving after first paint, a store
  // pull). That's deliberate: the user may already be mid-session, past the first card
  // in the queue, and a re-sync would yank their position. Fresher data helps only the
  // *next* time this frame mounts.
  const [stack, setStack] = useState<Frame[]>([makeFrame(frame.label, rootZenScope(frame), tasks)]);
  const [doneMessage] = useState(
    () => DONE_MESSAGES[Math.floor(Math.random() * DONE_MESSAGES.length)],
  );
  const top = stack[stack.length - 1];
  const current = top.tasks[top.index] ?? null;
  const otherBucket = current ? listOptions.find((l) => l.id !== current.listId) : undefined;
  const moveLabel = otherBucket && `Перенести ${otherBucket.kind === "inbox" ? "во" : "в"} «${otherBucket.name}»`;

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);
  const editConfirmedAt = useRef(0);

  useEffect(() => {
    setEditingTitle(false);
  }, [current?.id]);

  useEffect(() => {
    if (editingTitle && current) setTitleDraft(current.title);
  }, [editingTitle, current?.id, current?.title]);

  useEffect(() => {
    if (editingTitle) titleInputRef.current?.focus();
  }, [editingTitle]);

  const mergeCurrent = useCallback((patch: Partial<TaskData>) => {
    setStack((s) =>
      s.map((f, i) => {
        if (i !== s.length - 1) return f;
        const tasks = f.tasks.map((t, ti) =>
          ti === f.index ? { ...t, ...patch } : t,
        );
        return { ...f, tasks };
      }),
    );
  }, []);

  const settle = useCallback((id: string, patch: Partial<TaskData>) => {
    const today = userToday();
    setStack((s) => s.map((f) => settleInFrame(f, id, patch, today)));
  }, []);

  const saveTitle = useCallback(() => {
    if (!current) return;
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!next || next === current.title) return;
    updateTask(current.id, { title: next }, {
      onSuccess: () => settle(current.id, { title: next }),
    });
  }, [current, titleDraft, settle]);

  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditingNotes(false);
  }, [current?.id]);

  useEffect(() => {
    if (editingNotes && current) setNotesDraft(current.notes ?? "");
  }, [editingNotes, current?.id, current?.notes]);

  useEffect(() => {
    if (!editingNotes) return;
    const el = notesTextareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [editingNotes]);

  const saveNotes = useCallback(() => {
    if (!current) return;
    const next = notesDraft;
    setEditingNotes(false);
    if (next === (current.notes ?? "")) return;
    updateTask(current.id, { notes: next }, {
      onSuccess: () => mergeCurrent({ notes: next }),
    });
  }, [current, notesDraft, mergeCurrent]);

  const cancelNotes = useCallback(() => {
    setEditingNotes(false);
  }, []);

  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    setDatePickerOpen(false);
  }, [current?.id]);

  const saveDueOn = useCallback(
    (next: string | null) => {
      if (!current) return;
      updateTask(current.id, { due_on: next }, {
        onSuccess: () => settle(current.id, { dueOn: next }),
      });
    },
    [current, settle],
  );

  const breadcrumbs = useMemo(() => stack.map((f) => f.label).join(" › "), [stack]);
  const total = top.tasks.length;
  const progress = total === 0 ? 1 : top.completedCount / total;

  const advance = useCallback(() => {
    setStack((s) =>
      s.map((f, i) =>
        i === s.length - 1 ? { ...f, index: f.index + 1, completedCount: f.completedCount + 1 } : f,
      ),
    );
  }, []);

  const skip = useCallback(() => {
    if (!current) return;
    setStack((s) => {
      const next = [...s];
      const f = { ...next[next.length - 1] };
      const arr = [...f.tasks];
      const [moved] = arr.splice(f.index, 1);
      arr.push(moved);
      f.tasks = arr;
      next[next.length - 1] = f;
      return next;
    });
    const siblings = top.tasks.filter((t) => t.id !== current.id);
    const repositioned = [...siblings, current];
    const neighbors = neighborsForReorder(repositioned, current.id);
    reorderTask(current.id, neighbors);
  }, [current, top.tasks]);

  const complete = useCallback(() => {
    if (!current) return;
    mutateToggle(current);
    advance();
  }, [current, advance]);

  const remove = useCallback(() => {
    if (!current) return;
    if (!confirm(`Удалить «${current.title}»?`)) return;
    mutateDelete(current.id);
    advance();
  }, [current, advance]);

  const removeFromMyDay = useCallback(() => {
    if (!current) return;
    // In the My Day frame an unpinned task no longer belongs here — step past
    // it like complete/delete. In list/task/dived-in frames the task still
    // belongs, so just drop the My Day badge in place.
    const inMyDayFrame = frame.kind === "myDay" && stack.length === 1;
    updateTask(current.id, { my_day_on: null }, {
      ...(inMyDayFrame ? {} : { onSuccess: () => settle(current.id, { myDayOn: null }) }),
    });
    if (inMyDayFrame) advance();
  }, [current, settle, advance, frame.kind, stack.length]);

  const moveToList = useCallback(
    (listId: string) => {
      if (!current) return;
      // In the root My Day frame the task keeps its place — a bucket move doesn't change
      // its day. In a list frame it now belongs to
      // another list; in a dived-in (or task) frame it was a subtask and the move
      // promoted it out of the parent. Either way, step past it like complete/delete.
      const stays = frame.kind === "myDay" && stack.length === 1;
      updateTask(current.id, { list_id: listId }, {
        ...(stays ? { onSuccess: () => settle(current.id, { listId }) } : {}),
      });
      if (!stays) advance();
    },
    [current, settle, advance, frame.kind, stack.length],
  );

  const diveInToTask = useCallback(async (taskId: string, label: string) => {
    try {
      const res = await fetch(`/tasks/${taskId}`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const data = await res.json();
      const subtasks: TaskData[] = (data.subtasks ?? []).filter(
        (t: TaskData) => t.status === "pending",
      );
      if (subtasks.length === 0) return;
      setStack((s) => [...s, makeFrame(label, { kind: "children", parentId: taskId }, subtasks)]);
    } catch {
      /* swallow */
    }
  }, []);

  const diveIn = useCallback(async () => {
    if (!current) return;
    if ((current.childrenCount ?? 0) === 0) return;
    void diveInToTask(current.id, current.title);
  }, [current, diveInToTask]);

  const cur = current;
  const aiEntries: AiActionEntry[] = cur
    ? (() => {
        const anchor: AiAnchor = { kind: "task", id: cur.id };
        const diveIn = () => void diveInToTask(cur.id, cur.title);
        return taskActions.map((a) => ({
          id: a.id,
          name: a.name,
          confirm: a.confirm,
          run: () => aiRun(anchor, a.id, { onChange: diveIn }),
          preview: () => aiPreview(anchor, a.id),
          apply: (ops: PreviewOp[]) => aiApply(anchor, ops, { onChange: diveIn }),
        }));
      })()
    : [];

  const diveOut = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  useEffect(() => {
    if (top.index < top.tasks.length) return undefined;
    if (stack.length > 1) {
      const t = setTimeout(() => diveOut(), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [top.index, top.tasks.length, stack.length, diveOut]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (editingTitle || editingNotes || datePickerOpen) return;

      if (e.key === "Escape") {
        e.preventDefault();
        exitZen();
      } else if (e.key === "Enter") {
        // Auto-repeat from a held key, or the tail of a just-confirmed title
        // edit — neither should complete the task.
        if (e.repeat) return;
        if (Date.now() - editConfirmedAt.current < EDIT_CONFIRM_GRACE_MS) return;
        e.preventDefault();
        complete();
      } else if (e.key === " ") {
        e.preventDefault();
        skip();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [complete, skip, exitZen, editingTitle, editingNotes, datePickerOpen]);

  const allDone = stack.length === 1 && top.index >= top.tasks.length;

  return (
    <div className="zen-screen" data-testid="zen-screen">
      <ExitButton />

      <div className="zen-context">{breadcrumbs}</div>

      {!allDone && current && (
        <div className="zen-card" key={current.id}>
          <div className="zen-progress">
            <div className="zen-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>

          <div className="zen-body" data-testid="zen-body">
            {editingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                aria-label="Изменить заголовок"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    editConfirmedAt.current = Date.now();
                    saveTitle();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingTitle(false);
                  }
                }}
                className="zen-title zen-title-input"
              />
            ) : (
              <h2
                className="zen-title"
                onClick={() => setEditingTitle(true)}
              >
                <Linkified text={current.title} />
              </h2>
            )}

            {editingNotes ? (
              <textarea
                ref={notesTextareaRef}
                aria-label="Изменить заметки"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                onBlur={saveNotes}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    cancelNotes();
                  }
                }}
                placeholder={NOTES_PLACEHOLDER}
                className="zen-notes zen-notes-input"
                rows={4}
              />
            ) : current.notes ? (
              <p className="zen-notes" onClick={() => setEditingNotes(true)}>
                <Linkified text={current.notes} />
              </p>
            ) : null}

            <div className="zen-edit-ghost">
              {!current.notes && !editingNotes && (
                <button
                  type="button"
                  aria-label="Заметки"
                  title="Заметки"
                  onClick={() => setEditingNotes(true)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
              {current.myDayOn === todayIso() && (
                <button
                  type="button"
                  aria-label="Убрать из «Мой день»"
                  title="Убрать из «Мой день»"
                  onClick={removeFromMyDay}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </button>
              )}
              <QuickDuePicker
                value={current.dueOn ?? null}
                onChange={saveDueOn}
                onOpenChange={setDatePickerOpen}
              />
              {otherBucket && (
                <button
                  type="button"
                  onClick={() => moveToList(otherBucket.id)}
                  aria-label={moveLabel}
                  title={moveLabel}
                  className="zen-move-button"
                >
                  <BucketMoveIcon target={otherBucket.kind} />
                  <span aria-hidden="true">→ {otherBucket.name}</span>
                </button>
              )}
            </div>

            {(current.childrenCount ?? 0) > 0 && (
              <button className="zen-dive-link" onClick={diveIn} type="button">
                В этой задаче {subtaskCount(current.childrenCount ?? 0)} — погрузиться
              </button>
            )}

            {stack.length > 1 && (
              <button className="zen-dive-link" onClick={diveOut} type="button">
                ← Назад к «{stack[stack.length - 2].label}»
              </button>
            )}
          </div>

          <div className="zen-actions">
            <button
              className="zen-complete"
              onClick={complete}
              aria-label="Завершить"
              title="Завершить (Enter)"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button className="zen-skip" onClick={skip} aria-label="Пропустить" title="Пропустить (Space)">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7" />
              </svg>
            </button>
            {(current.childrenCount ?? 0) === 0 && (
              <AiActionMenu
                entries={aiEntries}
                side="top"
                ariaLabel="Разбить с ИИ"
                buttonClassName="zen-break-down disabled:opacity-60 disabled:cursor-default"
                iconClassName="w-5 h-5"
                spinnerClassName="block w-5 h-5 rounded-full border-2 border-current border-t-transparent animate-spin"
              />
            )}
            <button className="zen-delete" onClick={remove} aria-label="Удалить" title="Удалить">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {allDone && (
        <div className="zen-done" data-testid="zen-done">
          <p className="text-2xl text-gray-900 dark:text-white">{doneMessage}</p>
        </div>
      )}

      <div className="zen-hints">Enter — завершить · Space — пропустить · Esc — выйти</div>
    </div>
  );
}
